import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, Check, X, Minus, Trophy } from "lucide-react";
import DemoQRCode from "@/components/DemoQRCode";

// Comparison rows — DD advantage highlighted. Only claims about Kimoby that
// are publicly stated or genuinely differentiated. No unverifiable feature-shaming.
const ROWS = [
  {
    axis: "Founder background",
    kimoby: "Software company (est. 2013)",
    dd: "42-year fixed-ops veteran · 400-page Ayers Fixed Ops Academy",
    win: "dd",
  },
  {
    axis: "Decline follow-up",
    kimoby: "Standard SMS reminders",
    dd: "5-touch ladder: Offer 1 → Offer 2 → Final SMS → advisor call → manager escalation",
    win: "dd",
  },
  {
    axis: "Comeback Protocol (on your schedule)",
    kimoby: "Not offered",
    dd: "Auto final SMS · advisor call task · manager escalation — days set by you in Settings",
    win: "dd",
  },
  {
    axis: "MPI → SMS handoff",
    kimoby: "Manual customer contact",
    dd: "Yellow/red items become drip texts automatically — zero re-typing",
    win: "dd",
  },
  {
    axis: "Technician scorecard",
    kimoby: "Not offered",
    dd: "Per-tech dashboard — inspections, yellow flags, red flags, revenue tracked",
    win: "dd",
  },
  {
    axis: "Advisor commission tracker",
    kimoby: "Not offered",
    dd: "Monthly print-ready commission report — hand to whoever runs payroll",
    win: "dd",
  },
  {
    axis: "Advisor training program",
    kimoby: "Software onboarding",
    dd: "Ayers Fixed Ops Academy — 58 chapters, dealer-purchasable, real advisor certification",
    win: "dd",
  },
  {
    axis: "TCPA compliance",
    kimoby: "STOP handling",
    dd: "STOP handling + full inbound audit trail + admin opt-out list + signature validation",
    win: "tie",
  },
  {
    axis: "Onboarding speed",
    kimoby: "Multi-week implementation",
    dd: "Sending texts by Day 1 · white-glove included",
    win: "dd",
  },
  {
    axis: "Contract commitment",
    kimoby: "Annual",
    dd: "Month-to-month · founding dealer lifetime rate lock at $1,295/mo",
    win: "dd",
  },
  {
    axis: "Pricing transparency",
    kimoby: "Quote-based",
    dd: "Published rates · $895 onboarding · $1,295/mo · no surprises",
    win: "dd",
  },
  {
    axis: "Dealer data ownership",
    kimoby: "Vendor-managed",
    dd: "Dealer keeps 100% of customer + RO data · exportable any time",
    win: "dd",
  },
  {
    axis: "Custom SMS templates",
    kimoby: "Template library",
    dd: "Full-text control · Highest-Converting Templates highlighted card",
    win: "tie",
  },
  {
    axis: "Live pitch scarcity",
    kimoby: "Standard demo",
    dd: "\"founding dealer\" program · 4 lifetime slots · live reserve animation",
    win: "dd",
  },
];

function WinIcon({ win, side }) {
  if (win === "tie") return <Minus className="w-4 h-4 text-neutral-400" strokeWidth={2} />;
  if (win === side) return <Check className="w-5 h-5 text-emerald-600" strokeWidth={3} />;
  return <X className="w-4 h-4 text-neutral-400" strokeWidth={2} />;
}

export default function KimobyComparePage() {
  const ddWins = ROWS.filter((r) => r.win === "dd").length;
  const ties = ROWS.filter((r) => r.win === "tie").length;

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Head-to-Head Comparison" />

      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Competitor Comparison · Sales Handout</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Decline Drive vs Kimoby
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Hand this to the dealer principal when Kimoby comes up. Clean side-by-side, no shaming, focus on the differentiators only a 42-year fixed-ops veteran could build.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-kimoby-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      <Card className="p-6 border-[hsl(var(--gold))]/40 bg-gradient-to-br from-navy to-[#0a1d4a] text-white mb-6" data-testid="kimoby-hero">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[hsl(var(--gold))] grid place-items-center shrink-0">
            <Trophy className="w-5 h-5 text-neutral-900" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-mono-label text-[hsl(var(--gold-light))] text-[0.6rem] tracking-widest">
              Bottom line
            </div>
            <div className="font-display font-black text-xl md:text-2xl tracking-tight leading-tight">
              Same category. Different pedigree. {ddWins} of {ROWS.length} categories go to Decline Drive.
            </div>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden border-border">
        <div className="grid grid-cols-12 bg-navy text-white px-4 py-3 text-sm font-mono-label tracking-widest">
          <div className="col-span-4">CAPABILITY</div>
          <div className="col-span-4 text-center">KIMOBY</div>
          <div className="col-span-4 text-center bg-[hsl(var(--gold))]/20 -my-3 py-3 border-l-2 border-r-2 border-[hsl(var(--gold))]">
            DRIVELOGIC
          </div>
        </div>
        {ROWS.map((r, i) => (
          <div
            key={i}
            className={`grid grid-cols-12 items-center px-4 py-3 text-sm ${
              i % 2 === 0 ? "bg-white" : "bg-neutral-50"
            } ${r.win === "dd" ? "border-l-4 border-l-[hsl(var(--gold))]" : ""}`}
            data-testid={`kimoby-row-${i}`}
          >
            <div className="col-span-4 font-semibold text-neutral-900">{r.axis}</div>
            <div className="col-span-4 px-2 flex items-start gap-2">
              <WinIcon win={r.win} side="kimoby" />
              <div className="text-neutral-700 text-xs leading-snug">{r.kimoby}</div>
            </div>
            <div className={`col-span-4 px-2 flex items-start gap-2 ${r.win === "dd" ? "bg-[hsl(var(--gold))]/10 -mx-4 pl-6 pr-4 py-2 rounded-md" : ""}`}>
              <WinIcon win={r.win} side="dd" />
              <div className={`text-xs leading-snug ${r.win === "dd" ? "font-semibold text-neutral-900" : "text-neutral-700"}`}>
                {r.dd}
              </div>
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-5 border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5 mt-6 flex flex-col sm:flex-row items-center gap-4" data-testid="kimoby-close">
        <div className="flex-1">
          <div className="font-mono-label text-[hsl(var(--gold))] text-[0.6rem] tracking-widest">
            Why this matters
          </div>
          <div className="font-display font-black text-xl md:text-2xl tracking-tight mt-2 leading-tight">
            Kimoby was built by software people. Decline Drive was built by the guy who wrote the training manual.
          </div>
          <div className="text-sm text-neutral-700 mt-3">
            42 years in fixed ops. 400 pages of certified advisor training. Comeback Protocol built into the app. That's not features — that's a philosophy your service team will actually adopt.
          </div>
        </div>
        <DemoQRCode size={110} caption="Scan · see it in action" />
      </Card>

      <div className="text-[0.6rem] text-neutral-500 mt-4 max-w-3xl">
        Comparison based on publicly available information about Kimoby and Decline Drive's shipped features. Vendor capabilities change — verify current offerings before signing any contract.
      </div>
    </DashboardShell>
  );
}
